
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Product } from '@/types/portfolio';

interface ChemicalSafetyScorecardProps {
  product: Product;
}

interface EndpointData {
  name: string;
  score: 'low' | 'moderate' | 'high' | 'unknown';
  label: string;
}

interface ScorecardSection {
  title: string;
  endpoints: EndpointData[];
}

export const ChemicalSafetyScorecard = ({ product }: ChemicalSafetyScorecardProps) => {
  // Mock data for the scorecard - in a real app this would come from the database
  const overallConcern = 'moderate' as const;
  const warningIcons = ['skin', 'environment'] as const;
  
  const sections: ScorecardSection[] = [
    {
      title: 'Immediate Health Effects',
      endpoints: [
        { name: 'Skin irritation', score: 'moderate', label: 'MILD' },
        { name: 'Eye irritation', score: 'low', label: 'MINIMAL' },
        { name: 'Breathing hazard', score: 'low', label: 'LOW' },
        { name: 'Swallowing hazard', score: 'low', label: 'LOW' },
        { name: 'Skin allergy risk', score: 'moderate', label: 'POSSIBLE' },
      ]
    },
    {
      title: 'Long-term Health Effects',
      endpoints: [
        { name: 'Cancer risk', score: 'low', label: 'LOW' },
        { name: 'DNA damage', score: 'low', label: 'NEGATIVE' },
        { name: 'Reproductive harm', score: 'unknown', label: 'UNKNOWN' },
        { name: 'Organ damage', score: 'low', label: 'LOW' },
        { name: 'Hormone disruption', score: 'unknown', label: 'UNKNOWN' },
      ]
    },
    {
      title: 'Environmental Impact',
      endpoints: [
        { name: 'Fish toxicity', score: 'moderate', label: 'MODERATE' },
        { name: 'Water organisms', score: 'moderate', label: 'MODERATE' },
        { name: 'Soil organisms', score: 'low', label: 'LOW' },
        { name: 'Breaks down naturally', score: 'low', label: 'YES' },
        { name: 'Builds up in animals', score: 'low', label: 'NO' },
      ]
    },
    {
      title: 'Physical Hazards',
      endpoints: [
        { name: 'Fire risk', score: 'low', label: 'LOW' },
        { name: 'Explosion risk', score: 'low', label: 'NO' },
        { name: 'Chemical burns', score: 'low', label: 'NO' },
      ]
    }
  ];

  const getScoreColor = (score: string) => {
    switch (score) {
      case 'low': return 'bg-green-500';
      case 'moderate': return 'bg-amber-500';
      case 'high': return 'bg-red-500';
      case 'unknown': return 'bg-gray-500';
      default: return 'bg-gray-500';
    }
  };

  const getOverallConcernColor = (concern: string) => {
    switch (concern) {
      case 'low': return 'text-green-600';
      case 'moderate': return 'text-amber-600';
      case 'high': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  const getWarningIcon = (type: string) => {
    const iconClasses = "px-2 py-1 rounded-full text-xs font-bold text-white";
    switch (type) {
      case 'carcinogen':
        return <div className={`${iconClasses} bg-red-600`} title="May cause cancer">CANCER RISK</div>;
      case 'reproductive':
        return <div className={`${iconClasses} bg-amber-800`} title="May harm fertility or unborn child">REPRODUCTIVE HARM</div>;
      case 'environment':
        return <div className={`${iconClasses} bg-green-600`} title="Harmful to aquatic life">AQUATIC HAZARD</div>;
      case 'skin':
        return <div className={`${iconClasses} bg-orange-600`} title="May cause skin irritation">SKIN IRRITANT</div>;
      default:
        return null;
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Chemical Safety Analysis</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="max-w-md mx-auto border-2 border-black bg-white font-sans shadow-lg">
          {/* Header */}
          <div className="bg-black text-white p-2 text-center font-bold text-lg">
            Chemical Safety Facts
          </div>
          
          {/* Chemical Name */}
          <div className="bg-gray-50 p-3 border-b border-gray-300 text-center">
            <div className="font-bold text-base">{product.name}</div>
            <div className="text-xs text-gray-600 mt-1">
              CAS: 123-45-6 | {product.product_type?.replace('_', ' ')}
            </div>
          </div>
          
          {/* Overall Score */}
          <div className="p-4 bg-gray-50 text-center">
            <div className={`text-2xl font-bold ${getOverallConcernColor(overallConcern)}`}>
              {overallConcern.toUpperCase()} CONCERN
            </div>
            <div className="flex justify-center gap-2 mt-2 flex-wrap">
              {warningIcons.map((icon, index) => (
                <div key={index}>
                  {getWarningIcon(icon)}
                </div>
              ))}
            </div>
          </div>
          
          {/* Sections */}
          {sections.map((section, sectionIndex) => (
            <div key={sectionIndex} className="p-3 border-b border-gray-300 last:border-b-0">
              <div className="font-bold text-sm text-gray-700 mb-2 pb-1 border-b border-gray-300">
                {section.title}
              </div>
              {section.endpoints.map((endpoint, endpointIndex) => (
                <div key={endpointIndex} className="flex justify-between items-center py-1 text-xs">
                  <span className="text-gray-600 flex-1">{endpoint.name}</span>
                  <div className={`w-16 h-5 rounded-full text-center text-white font-bold text-xs leading-5 ml-2 ${getScoreColor(endpoint.score)}`}>
                    {endpoint.label}
                  </div>
                </div>
              ))}
            </div>
          ))}
          
          {/* Legend */}
          <div className="p-2 text-xs bg-gray-100 border-t border-gray-300">
            <div className="flex flex-wrap gap-3 mb-2">
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-full bg-green-500 mr-1"></div>
                Low/Minimal Risk
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-full bg-amber-500 mr-1"></div>
                Moderate Risk
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-full bg-red-500 mr-1"></div>
                High Risk
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-full bg-gray-500 mr-1"></div>
                Unknown/No Data
              </div>
            </div>
            
            <div className="text-gray-600 text-center mt-2">
              Data Quality: High | Last Updated: 2025 | Based on: Animal studies, lab tests, computer models
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
